// ResetPassword.tsx — public page reached from the reset email link
// (/reset/:token). Validates the two password fields locally, then posts the
// token + new password to the confirm endpoint, surfacing the backend `detail`
// (e.g. expired/invalid token) on failure.

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Field, Input } from '@/foundation/primitives';
import { confirmPasswordReset } from '@/auth/passwordReset';
import { PASSWORD_POLICY_NOTE, passwordPolicyError } from '@/auth/passwordPolicy';
import { AuthShell, authErrorBox, authLink } from './AuthShell';

export default function ResetPassword() {
  const { token } = useParams<{ token: string }>();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) return;
    const policyError = passwordPolicyError(password);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthShell
        title="Invalid reset link."
        deck="This password reset link is invalid or incomplete."
        footer={
          <Link to="/forgot-password" style={authLink}>
            Request a new link
          </Link>
        }
      >
        <p className="body2" style={{ margin: 0, color: 'var(--text-secondary)' }}>
          Reset links are single-use and expire — request a fresh one to continue.
        </p>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password updated." deck="You can now sign in with your new password.">
        <Link to="/login" style={{ textDecoration: 'none' }}>
          <Button style={{ width: '100%', justifyContent: 'center', height: 44 }}>
            Go to sign in
          </Button>
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password."
      deck={`Enter and confirm your new password · ${PASSWORD_POLICY_NOTE}`}
      footer={
        <Link to="/login" style={authLink}>
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="New password">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="••••••••"
          />
        </Field>

        <Field label="Confirm password">
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            placeholder="••••••••"
          />
        </Field>

        {error && <div style={authErrorBox}>{error}</div>}

        <Button
          disabled={submitting || !password || !confirm}
          style={{ width: '100%', justifyContent: 'center', height: 44 }}
        >
          {submitting ? 'Updating…' : 'Reset password'}
        </Button>
      </form>
    </AuthShell>
  );
}
